import { useQuery, useQueryClient } from '@tanstack/react-query'
import { DataTable, useDataTableContext } from 'components/DataTable'
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react'
import { Spinner } from 'react-bootstrap'
import apiService from 'services/api'
import * as S from '../CommonTable.styles'
import { TableSearch } from '../../Filters/TableSearch'
import { getTableQueryKey } from '../../../utils/getTableQueryKey'
import styled from 'styled-components'
import { ColumnsModal } from '../../Modals/ColumnsModal/ColumnsModal'
import { ContactResponse } from 'models/contacts'
import CreateDealModal from '../../Deals/CreateDealModal/CreateDealModal'
import TableButtonAction from 'components/Buttons/TableButtons/TableButtonAction'
import TagAssignModal from 'components/Modals/AssignmentModal/TagAssignModal'
import DistributorAssignModal from 'components/Modals/AssignmentModal/DistributorAssignModal'
import { usePaginationURLParams } from 'utils/usePaginationURLParams'
import { BsBriefcase, BsTags, BsTruck } from 'react-icons/bs'
import { ColumnDef } from '@tanstack/react-table'
import { IContactCompany } from '../../../models/contact_companies'
import {
  AccountOwnerCell,
  DistributorsCell,
  SaleStagesCellFromDeals,
  TaglistCell,
} from '../../FbUI/StagePill'
import dayjs from 'dayjs'
import { FbLink } from 'components/FbUI/FbLink'
import { CompaniesFilterset } from 'components/Tables/CompaniesTable/CompaniesFilterset'
import {
  filterStoreRepo,
  useFilterParams,
} from 'components/Filters/FilterStore'
import FilterChips from 'components/Filters/components/FilterChips'
import ContactCompanyExportModal from 'components/Modals/ExportModal/ContactCompanyExportModal'
import { LinkCell } from '../CommonTableCells/LinkCell'
import { getCompanyUrl } from 'utils/getCompanyUrl'
import { usePreferences } from 'context/preferences/PreferencesContext'
import { IColumnSort } from 'components/DataTable/types'
import { Category, SubCategory } from 'models/companies'
import OpportunitiesTableFooterControls from 'components/Opportunities/OpportunitiesTableFooterControls'
import { UGC_TABLE_KEY } from 'constants/tableQueryKeys'

type SortableFields = (keyof ContactResponse | string)[]

export type ContactsTableRef = {
  refetch: () => void
}

const sortableFields: SortableFields = [
  'name',
  'created',
  'note_count',
  'deal_count',
  'contact_count',
  'sales_stages',
  'state',
  'city',
  'country',
]

const PAGE_SIZE = 20

const CompaniesTableComponent = forwardRef(
  (
    {
      setTotalRowsCount,
      pageParamName = 'page',
      category,
      subCategory,
    }: {
      setTotalRowsCount?: (count: number) => void
      pageParamName?: string
      category?: Category
      subCategory?: SubCategory
    },
    ref
  ) => {
    const tableContext = useDataTableContext()
    const queryClient = useQueryClient()
    const {
      state: { sorting, tableKey, rowSelection, totalSelectedRows },
      methods: { clearSelectedRows, setTotalRowsInBackend },
    } = useDataTableContext()

    const companiesFilterStore = filterStoreRepo.getStore(
      `companies-${subCategory?.id}`
    )

    const [pagination, setPagination] = usePaginationURLParams(
      PAGE_SIZE,
      pageParamName
    )
    const api = apiService()

    const filterParams = useFilterParams(companiesFilterStore)

    const sortParams = useMemo(() => {
      const params: Record<string, unknown> = {}

      if (sorting?.length) {
        params['sort'] = sorting[0].desc ? '-' + sorting[0]?.id : sorting[0]?.id
      }

      return params
    }, [sorting])

    const filterAndSortParams = useMemo(
      () => ({
        sub_category_id: subCategory?.id,
        ...filterParams,
        ...sortParams,
      }),
      [filterParams, sortParams]
    )

    const TABLE_QUERY_KEY = getTableQueryKey({
      tableKey: tableKey,
      filterParams: filterAndSortParams,
      page: pagination.pageIndex + 1,
    })

    // DATA FETCHING
    const { isLoading, data, isFetching, refetch } = useQuery({
      queryKey: [UGC_TABLE_KEY, ...TABLE_QUERY_KEY],
      queryFn: async () => {
        await queryClient.cancelQueries(TABLE_QUERY_KEY)
        clearSelectedRows()

        const res = await api.getCompaniesList(subCategory?.id as number, {
          page: pagination.pageIndex + 1,
          limit: pagination.pageSize,
          ...filterAndSortParams,
        })
        setTotalRowsCount?.(res.count)
        setTotalRowsInBackend(res.count)
        return res
      },
      keepPreviousData: true,
      staleTime: 1000 * 60 * 5,
    })
    // END DATA FETCHING

    useEffect(() => {
      clearSelectedRows()
    }, [data])

    const [showAssignTags, setShowAssignTags] = useState(false)
    const [showAssignDistributors, setShowAssignDistributors] = useState(false)
    const [openDealModal, setOpenDealModal] = React.useState(false)
    const [dealCompanuIds, setDealCompanyIds] = React.useState<number[]>([])
    const [showExportAllModal, setShowExportAllModal] = useState(false)

    const handleCreateDeals = () => {
      if (!data) return
      const companyIds = Object.keys(rowSelection).map(
        (idx) => data.results[parseInt(idx)].id
      )
      setDealCompanyIds(companyIds)
      setOpenDealModal(true)
    }
    // Make refetch available to parent
    useImperativeHandle(
      ref,
      () =>
        ({
          refetch: () => refetch(),
        }) as ContactsTableRef
    )

    const columns = useCompaniesTableCols({
      sorting,
    })

    useEffect(() => {
      setTotalRowsCount?.(data?.count ?? 0)
    }, [data?.count])

    return (
      <TableContainer>
        <S.SearchContainer>
          <S.SearchContainerFilters>
            <S.TableSearchContainer>
              <TableSearch
                filterStore={companiesFilterStore}
                searchPlaceholder="Search by name, company..."
              />
            </S.TableSearchContainer>
            <S.TableButtonsContainer>
              <CompaniesFilterset subCategoryId={subCategory?.id as number} />
              <ColumnsModal />
              <TableButtonAction
                items={[
                  {
                    label: 'Create Deals',
                    icon: <BsBriefcase size={20} />,
                    callback: handleCreateDeals,
                  },
                  {
                    label: 'Manage Tags',
                    icon: <BsTags size={20} />,
                    callback: () => setShowAssignTags(true),
                  },
                  {
                    label: 'Add Known Distributors',
                    icon: <BsTruck size={20} />,
                    callback: () => setShowAssignDistributors(true),
                  },
                ]}
                disabled={!totalSelectedRows}
                selectedRowsCount={totalSelectedRows ?? 0}
              />
            </S.TableButtonsContainer>
          </S.SearchContainerFilters>
          <FilterChips clearAllButton showActive store={companiesFilterStore} />
        </S.SearchContainer>

        <TagAssignModal
          show={showAssignTags}
          handleClose={() => setShowAssignTags(false)}
          tableQueryKey={TABLE_QUERY_KEY}
          data={data?.results ?? []}
          filterAndSortParams={filterAndSortParams}
          apiTagsAction={api.companiesBulkEditTags.bind(
            this,
            subCategory?.id as number
          )}
          idAccessor="id"
        />

        <DistributorAssignModal
          show={showAssignDistributors}
          handleClose={() => setShowAssignDistributors(false)}
          tableQueryKey={TABLE_QUERY_KEY}
          data={data?.results ?? []}
          idAccessor="id"
          filterAndSortParams={filterAndSortParams}
          apiDistributorsAction={api.companiesBulkEditDistributors.bind(
            this,
            subCategory?.id as number
          )}
        />

        <ContactCompanyExportModal
          count={data?.count ?? 0}
          show={showExportAllModal}
          _handleClose={() => {
            setShowExportAllModal(false)
          }}
          subCategoryName={subCategory?.name as string}
          filters={filterAndSortParams}
        />

        <DataTable
          key={tableContext.state.tableKey}
          autoLayout
          data={data?.results ?? []}
          columns={columns}
          sortableFields={sortableFields}
          virtualizeRows={false}
          enableRowSelection
          isPaginationEnabled={true}
          paginationOptions={{
            pageCount: Math.ceil((data?.count ?? 0) / PAGE_SIZE),
            setPagination: setPagination,
            pagination: pagination,
            isPaginationLoading: isLoading,
          }}
        >
          <DataTable.FooterControls>
            <OpportunitiesTableFooterControls
              categoryId={category?.id}
              subCategoryId={subCategory?.id}
              onDownloadButtonClick={() => setShowExportAllModal(true)}
            />
          </DataTable.FooterControls>
        </DataTable>

        <LoadingOverlay show={isFetching}>
          <Spinner />
        </LoadingOverlay>

        {openDealModal && (
          <CreateDealModal
            origin="companies"
            isUserCompany={true}
            companyIds={dealCompanuIds}
            show={openDealModal}
            handleClose={() => setOpenDealModal(false)}
            queryKey={TABLE_QUERY_KEY}
            filterAndSortParams={filterAndSortParams}
          />
        )}
      </TableContainer>
    )
  }
)
CompaniesTableComponent.displayName = 'CompaniesTable'

export const CompaniesTable = CompaniesTableComponent

export const useCompaniesTableCols = ({
  sorting,
}: {
  sorting?: IColumnSort<object>[]
}) => {
  const { preferences } = usePreferences()
  const tooltips: Record<string, unknown> = preferences?.tooltips || {}

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns: ColumnDef<IContactCompany, any>[] = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        size: 250,
        cell: (info) => (
          <FbLink to={getCompanyUrl(info.row.original)} target="_blank">
            {info.row.original.name}
          </FbLink>
        ),
      },
      {
        accessorKey: 'created',
        header: 'Date Created',
        size: 150,
        cell: (info) => dayjs(info.getValue()).format('MM/DD/YYYY'),
      },
      {
        id: 'note_count',
        header: 'Notes',
        size: 150,
        cell: (info) => {
          return info.row.original.note_count || '-'
        },
      },
      {
        accessorKey: 'country',
        header: 'Country',
      },
      {
        accessorKey: 'state',
        header: 'State',
        cell: (info) => {
          return info.getValue()
        },
      },
      {
        accessorKey: 'city',
        header: 'City',
      },
      {
        id: 'deal_count',
        header: 'Deals',
        size: 150,
        cell: (info) => {
          return info.row.original.deals?.length || '-'
        },
      },

      {
        id: 'sales_stages',
        header: 'Sales Stages',
        size: 260,
        cell: (info) => {
          return (
            <SaleStagesCellFromDeals
              deals={info.row.original?.deals}
              sortAsc={
                sorting?.length && sorting[0].id === 'sales_stages'
                  ? !sorting[0].desc
                  : undefined
              }
            />
          )
        },
      },

      {
        id: 'account_owner',
        header: 'Account Owners',
        size: 260,
        cell: (info) => {
          const deals = info.row.original?.deals
          return <AccountOwnerCell deals={deals} />
        },
      },

      {
        id: 'taglist',
        header: 'Tags',
        size: 260,
        cell: (info) => {
          return <TaglistCell taglist={info.row.original?.taglist} />
        },
      },
      {
        accessorKey: 'distributors',
        header: 'Known Distributors',
        size: 260,
        cell: (info) => {
          return (
            <DistributorsCell distributors={info.row.original?.distributors} />
          )
        },
      },
      {
        accessorKey: 'google_place_url',
        meta: { tooltip: tooltips?.['opps_google_place_url'] },
        header: 'Place URL',
        size: 150,
        cell: (info) => (info.getValue() ? <LinkCell info={info} /> : '-'),
      },
    ],
    [tooltips, sorting]
  )
  return columns
}

const TableContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  overflow: hidden;
`

const LoadingOverlay = styled.div<{
  show: boolean
}>`
  display: ${({ show }) => (show ? 'flex' : 'none')};
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(1px);
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  z-index: 30;
`
